import type { components } from '@adiox/api';

export type Bar = components['schemas']['BarDto'];

/** All 21 of MT5's timeframes, in MT5's order. */
export const TIMEFRAMES = [
  'M1',
  'M2',
  'M3',
  'M4',
  'M5',
  'M6',
  'M10',
  'M12',
  'M15',
  'M20',
  'M30',
  'H1',
  'H2',
  'H3',
  'H4',
  'H6',
  'H8',
  'H12',
  'D1',
  'W1',
  'MN1',
] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

/** The nine on MT5's toolbar (keys 1 to 9); the others are one click further. */
export const STANDARD_TIMEFRAMES = [
  'M1',
  'M5',
  'M15',
  'M30',
  'H1',
  'H4',
  'D1',
  'W1',
  'MN1',
] as const satisfies readonly Timeframe[];

export const CHART_TYPES = ['candles', 'bars', 'line', 'area'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

const SECONDS: Record<Exclude<Timeframe, 'MN1'>, number> = {
  M1: 60,
  M2: 120,
  M3: 180,
  M4: 240,
  M5: 300,
  M6: 360,
  M10: 600,
  M12: 720,
  M15: 900,
  M20: 1200,
  M30: 1800,
  H1: 3600,
  H2: 7200,
  H3: 10_800,
  H4: 14_400,
  H6: 21_600,
  H8: 28_800,
  H12: 43_200,
  D1: 86_400,
  W1: 604_800,
};
const WEEK_ORIGIN = 3 * 86_400; // MT5 weeks start on Sunday; the epoch (a Thursday) is three days after one

/** Length of a bar in seconds; a month is taken as 30 days (only used to extend drawings and indicators past the last bar). */
export const timeframeSeconds = (tf: Timeframe): number => (tf === 'MN1' ? 2_592_000 : SECONDS[tf]);

export const isTimeframe = (value: unknown): value is Timeframe =>
  typeof value === 'string' && (TIMEFRAMES as readonly string[]).includes(value);
export const isChartType = (value: unknown): value is ChartType =>
  typeof value === 'string' && (CHART_TYPES as readonly string[]).includes(value);

/** Start (server-time epoch seconds) of the bar that contains `seconds`. */
export function barStart(tf: Timeframe, seconds: number): number {
  if (tf === 'MN1') {
    const d = new Date(seconds * 1000);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000;
  }
  if (tf === 'W1') return Math.floor((seconds - WEEK_ORIGIN) / SECONDS.W1) * SECONDS.W1 + WEEK_ORIGIN;
  return Math.floor(seconds / SECONDS[tf]) * SECONDS[tf];
}

/**
 * Folds a tick into the bar series. Bars are built from the bid, like MT5. A tick inside the last bar updates it; a tick in a
 * later period opens a new one; a tick older than the last bar is ignored (history is the server's, not the client's).
 * Returns the same array when nothing changed, otherwise a new one whose last element is the updated bar.
 */
export function applyTick(bars: Bar[], tf: Timeframe, bid: number, serverMs: number): Bar[] {
  const start = barStart(tf, Math.floor(serverMs / 1000));
  const last = bars[bars.length - 1];
  if (!last) return [{ t: start, o: bid, h: bid, l: bid, c: bid, tv: 1, sp: 0 }];
  if (start < last.t) return bars;
  if (start === last.t) {
    if (last.c === bid && last.h >= bid && last.l <= bid) return bars.with(-1, { ...last, tv: last.tv + 1 });
    return bars.with(-1, {
      ...last,
      h: Math.max(last.h, bid),
      l: Math.min(last.l, bid),
      c: bid,
      tv: last.tv + 1,
    });
  }
  return [...bars, { t: start, o: bid, h: bid, l: bid, c: bid, tv: 1, sp: 0 }];
}

/** Puts older bars in front of the series, dropping any overlap, so a page loaded while scrolling back joins cleanly. */
export function prependBars(bars: Bar[], older: Bar[]): Bar[] {
  const first = bars[0]?.t ?? Number.POSITIVE_INFINITY;
  const fresh = older.filter((b) => b.t < first);
  return fresh.length === 0 ? bars : [...fresh, ...bars];
}

/**
 * Replaces the tail of the series with a fresh server copy (after a reconnect, or when the live bar drifted), keeping
 * everything older. Server bars win wherever they overlap.
 */
export function mergeLatest(bars: Bar[], latest: Bar[]): Bar[] {
  const first = latest[0]?.t;
  if (first === undefined) return bars;
  return [...bars.filter((b) => b.t < first), ...latest];
}

export interface LegendValues {
  o: number;
  h: number;
  l: number;
  c: number;
  change: number;
  changePct: number;
}

/** The OHLC read-out for a bar: the change is close against open, as MT5's data window shows. */
export function legendOf(bar: Bar): LegendValues {
  const change = bar.c - bar.o;
  return {
    o: bar.o,
    h: bar.h,
    l: bar.l,
    c: bar.c,
    change,
    changePct: bar.o === 0 ? 0 : (change / bar.o) * 100,
  };
}

/** The price scale's decimals for a symbol. */
export function precisionFor(digits: number): { precision: number; minMove: number } {
  return { precision: digits, minMove: 1 / 10 ** digits };
}

/** How many bars to ask for when first opening a chart and when paging back. */
export const INITIAL_BARS = 1500;
export const PAGE_BARS = 1500;
/** Load an older page when fewer than this many bars are left off-screen on the left. */
export const PAGE_THRESHOLD = 50;
